import React, { useState, useEffect, type ReactNode } from 'react';
import { Mail, FileText } from 'lucide-react';
import { B2BMarketingPageLayout } from '../_shared/B2BMarketingPageLayout';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface LegalSection {
  id: string;
  heading: string;
  body: ReactNode;
}

export interface LegalDocLayoutProps {
  category: string;
  title: string;
  subtitle: string;
  effectiveDate: string;
  lastUpdated: string;
  version?: string;
  sections: LegalSection[];
  contactEmail?: string;
}

// ─── Typography helpers ───────────────────────────────────────────────────────

/** Standard body paragraph */
export function LP({ children }: { children: ReactNode }) {
  return (
    <p className="mb-4 text-[15px] leading-[1.75] text-gray-700 dark:text-zinc-300">{children}</p>
  );
}

/** Sub-section heading (h3) — highlighted label bar */
export function LH3({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-3 mt-7 inline-flex items-center gap-2 rounded-md bg-gray-100/80 px-3 py-1.5 text-[13px] font-semibold uppercase tracking-wide text-gray-800 dark:bg-zinc-800/70 dark:text-zinc-200">
      {children}
    </h3>
  );
}

/** Bulleted list */
export function LUl({ children }: { children: ReactNode }) {
  return (
    <ul className="mb-4 space-y-2 pl-0 text-[15px] text-gray-700 dark:text-zinc-300 [list-style:none]">
      {children}
    </ul>
  );
}

/**
 * Ordered list — intercepts LLi children and renders each with an
 * auto-incrementing number badge instead of the dot bullet.
 */
export function LOl({ children }: { children: ReactNode }) {
  const kids = React.Children.toArray(children);
  return (
    <ol className="mb-4 space-y-2 pl-0 text-[15px] text-gray-700 dark:text-zinc-300 [list-style:none]">
      {kids.map((child, i) => {
        const content =
          React.isValidElement(child)
            ? (child.props as { children?: ReactNode }).children
            : child;
        return (
          <li key={i} className="flex gap-3 leading-[1.7]">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-100 text-[11px] font-bold tabular-nums text-violet-700 dark:bg-violet-900/50 dark:text-violet-300">
              {i + 1}
            </span>
            <span className="flex-1">{content}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** List item — works inside LUl (dot bullet) */
export function LLi({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-2.5 leading-[1.7]">
      <span
        className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-violet-400 dark:bg-violet-500"
        aria-hidden
      />
      <span className="flex-1">{children}</span>
    </li>
  );
}

/** Callout / note block */
export function LNote({ children }: { children: ReactNode }) {
  return (
    <div className="my-5 flex gap-3 rounded-lg border-l-4 border-violet-400 bg-violet-50 px-4 py-4 dark:border-violet-600 dark:bg-violet-950/30">
      <div className="mt-0.5 shrink-0 text-violet-500 dark:text-violet-400">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
          <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm.75 10.5h-1.5v-4.5h1.5v4.5zm0-6h-1.5v-1.5h1.5v1.5z"/>
        </svg>
      </div>
      <p className="text-sm leading-relaxed text-violet-900 dark:text-violet-200">{children}</p>
    </div>
  );
}

/** Data table */
export function LTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}) {
  return (
    <div className="my-5 overflow-x-auto rounded-xl border border-gray-200 shadow-sm dark:border-zinc-700">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2 border-gray-200 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800/80">
            {headers.map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-left text-[12px] font-bold uppercase tracking-wide text-gray-600 dark:text-zinc-300"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
          {rows.map((row, i) => (
            <tr
              key={i}
              className={
                i % 2 === 1
                  ? 'bg-gray-50/60 dark:bg-zinc-900/30'
                  : 'bg-white dark:bg-transparent'
              }
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  className={`px-4 py-3 text-[14px] leading-relaxed text-gray-700 dark:text-zinc-300 ${
                    j === 0 ? 'font-medium text-gray-900 dark:text-zinc-100' : ''
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main layout ──────────────────────────────────────────────────────────────
export function LegalDocLayout({
  category,
  title,
  subtitle,
  effectiveDate,
  lastUpdated,
  version,
  sections,
  contactEmail = 'legal@gograbjob.com',
}: LegalDocLayoutProps) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? '');

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveId(id);
        },
        { rootMargin: '-15% 0% -70% 0%' },
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [sections]);

  return (
    <B2BMarketingPageLayout>
      {/* ── Document Header (no hero, no gradients) ── */}
      <header className="border-b border-gray-200 bg-white px-4 py-8 dark:border-zinc-800 dark:bg-zinc-950 sm:px-6 md:px-8">
        <div className="mx-auto max-w-6xl">
          {/* Breadcrumb label */}
          <div className="mb-4 flex items-center gap-2">
            <FileText className="h-4 w-4 text-violet-500" aria-hidden />
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-violet-600 dark:text-violet-400">
              {category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
            {title}
          </h1>

          {/* Subtitle */}
          <p className="mt-2 max-w-3xl text-base text-gray-600 dark:text-zinc-400">{subtitle}</p>

          {/* Meta row */}
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-gray-100 pt-4 dark:border-zinc-800">
            <span className="text-sm text-gray-500 dark:text-zinc-500">
              <span className="font-medium text-gray-700 dark:text-zinc-300">Effective:</span>{' '}
              {effectiveDate}
            </span>
            <span className="hidden h-4 w-px bg-gray-200 dark:bg-zinc-700 sm:block" aria-hidden />
            <span className="text-sm text-gray-500 dark:text-zinc-500">
              <span className="font-medium text-gray-700 dark:text-zinc-300">Last updated:</span>{' '}
              {lastUpdated}
            </span>
            {version ? (
              <>
                <span className="hidden h-4 w-px bg-gray-200 dark:bg-zinc-700 sm:block" aria-hidden />
                <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-semibold text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                  v{version}
                </span>
              </>
            ) : null}
            <span className="hidden h-4 w-px bg-gray-200 dark:bg-zinc-700 sm:block" aria-hidden />
            <span className="text-sm text-gray-500 dark:text-zinc-500">
              Zykes Technologies LLP
            </span>
          </div>
        </div>
      </header>

      {/* ── Page body ── */}
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 md:px-8">
        <div className="flex gap-10 lg:gap-16">

          {/* ── Sticky TOC ── */}
          <aside className="hidden w-56 shrink-0 lg:block">
            <div className="sticky top-28">
              <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 dark:text-zinc-500">
                Contents
              </p>
              <nav className="space-y-0.5">
                {sections.map(({ id, heading }, i) => (
                  <a
                    key={id}
                    href={`#${id}`}
                    className={`flex items-start gap-2 rounded-md px-2.5 py-1.5 text-[12px] leading-snug transition-colors duration-150 ${
                      activeId === id
                        ? 'bg-violet-50 font-semibold text-violet-700 dark:bg-violet-950/40 dark:text-violet-300'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800 dark:text-zinc-500 dark:hover:bg-zinc-800/50 dark:hover:text-zinc-300'
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById(id)
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      setActiveId(id);
                    }}
                  >
                    <span
                      className={`mt-px shrink-0 tabular-nums text-[10px] ${
                        activeId === id
                          ? 'text-violet-500 dark:text-violet-400'
                          : 'text-gray-400 dark:text-zinc-600'
                      }`}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span>{heading}</span>
                  </a>
                ))}
              </nav>

              {/* Contact card */}
              <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-zinc-700 dark:bg-zinc-900">
                <p className="text-[11px] font-bold uppercase tracking-wide text-gray-600 dark:text-zinc-400">
                  Questions?
                </p>
                <p className="mt-1 text-[12px] leading-snug text-gray-500 dark:text-zinc-500">
                  Contact our legal team:
                </p>
                <a
                  href={`mailto:${contactEmail}`}
                  className="mt-2 flex items-center gap-1.5 text-[12px] font-medium text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 break-all"
                >
                  <Mail className="h-3 w-3 shrink-0" />
                  {contactEmail}
                </a>
              </div>
            </div>
          </aside>

          {/* ── Document content ── */}
          <main className="min-w-0 flex-1">
            <div className="space-y-0">
              {sections.map(({ id, heading, body }, i) => (
                <section
                  key={id}
                  id={id}
                  className="scroll-mt-28 border-b border-gray-100 py-10 first:pt-0 dark:border-zinc-800/60"
                >
                  {/* Section heading with left accent bar */}
                  <div className="mb-6 flex items-start gap-4">
                    <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-violet-100 text-[11px] font-bold tabular-nums text-violet-700 dark:bg-violet-900/50 dark:text-violet-300">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <h2 className="text-xl font-bold leading-tight tracking-tight text-gray-900 dark:text-white sm:text-2xl">
                      {heading}
                    </h2>
                  </div>

                  {/* Body */}
                  <div className="pl-11">{body}</div>
                </section>
              ))}
            </div>

            {/* Document footer */}
            <div className="mt-10 rounded-xl border border-gray-200 bg-gray-50 px-6 py-5 dark:border-zinc-700 dark:bg-zinc-900/60">
              <p className="text-[13px] leading-relaxed text-gray-500 dark:text-zinc-500">
                This document was last updated on{' '}
                <strong className="text-gray-700 dark:text-zinc-300">{lastUpdated}</strong> and is
                effective from{' '}
                <strong className="text-gray-700 dark:text-zinc-300">{effectiveDate}</strong>. For
                questions or concerns, contact{' '}
                <a
                  href={`mailto:${contactEmail}`}
                  className="text-violet-600 hover:underline dark:text-violet-400"
                >
                  {contactEmail}
                </a>
                . © {new Date().getFullYear()} Zykes Technologies LLP. All rights reserved.
              </p>
            </div>
          </main>
        </div>
      </div>
    </B2BMarketingPageLayout>
  );
}
